/**
 * 全屏预览成片态
 *
 * 顶部"预览成片"按钮触发，展示：
 * - 大尺寸视频播放器
 * - 底部时间轴上的分镜缩略图（点击跳转）
 * - 拼接设置（折叠）
 * - 生成成片 / 下载 mp4 按钮
 *
 * 数据流：从 latest composition（如有）拿成片 URL；点"生成成片"触发新合成。
 */

import { useEffect, useState, useRef } from 'react';
import { ArrowLeft, Download, Film, Loader2, Settings, X } from 'lucide-react';
import toast from '../../../../components/Toast';
import {
  directorApi,
  DirectorApiError,
  type DirectorComposition,
} from '../../services/directorApi';
import { resolveBrowserMediaUrl } from '../../../../utils/storageUrl';

interface Props {
  open: boolean;
  projectId: string;
  onClose: () => void;
}

export function PreviewMode({ open, projectId, onClose }: Props) {
  const [composition, setComposition] = useState<DirectorComposition | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // 设置
  const [transitionType, setTransitionType] = useState<'none' | 'fade'>('fade');
  const [outputResolution, setOutputResolution] = useState<'auto' | '720p' | '1080p'>('auto');
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);
  // spec 16.6：管理员关闭站点水印时，前端水印开关也隐藏
  const [siteWatermarkEnabled, setSiteWatermarkEnabled] = useState(true);

  useEffect(() => {
    if (!open) return;
    directorApi
      .getPublicSettings()
      .then((res) => setSiteWatermarkEnabled(!!res.siteWatermarkEnabled))
      .catch(() => undefined);
  }, [open]);

  const reload = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const list = await directorApi.listCompositions(projectId);
      // 取最新的一条非 obsolete 的（按 createdAt desc）
      const latest = list[0] ?? null;
      setComposition(latest);
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  // 5 秒轮询：合成中或排队中时
  useEffect(() => {
    if (!open || !composition) return;
    if (composition.status === 'completed' || composition.status === 'failed') return;
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, composition]);

  const handleCreateComposition = async () => {
    setCreating(true);
    try {
      await directorApi.createComposition(projectId, {
        transitionType,
        outputResolution,
        watermarkEnabled,
      });
      toast.success('合成任务已开始');
      reload();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '创建失败');
    } finally {
      setCreating(false);
    }
  };

  const handleSeekToShot = (startMs: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = startMs / 1000;
      videoRef.current.play().catch(() => undefined);
    }
  };

  if (!open) return null;

  // 解析 timing snapshot
  let timing: Array<{ shotId: string; videoJobId: string; order: number; startMs: number; endMs: number }> = [];
  if (composition?.shotsTimingSnapshot) {
    try {
      timing = JSON.parse(composition.shotsTimingSnapshot);
    } catch {
      // ignore
    }
  }

  // 视频 URL：后端在 listCompositions 中已根据 storage provider 解析（公有桶为公开 URL，私有桶为短期签名 URL）
  const videoSrc = composition?.resultUrl ?? null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-950">
      {/* 顶部 bar */}
      <div className="flex items-center justify-between border-b border-white/10 bg-slate-950/90 px-4 py-3 text-white backdrop-blur-xl">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-2 rounded-2xl bg-white/10 px-3 py-1.5 text-sm transition hover:bg-white/20"
        >
          <ArrowLeft className="h-4 w-4" />
          返回故事板
        </button>
        <h2 className="text-base font-semibold">预览成片</h2>
        <button
          type="button"
          onClick={() => setShowSettings(!showSettings)}
          className="rounded-2xl bg-white/10 p-2 transition hover:bg-white/20"
          title="拼接设置"
        >
          <Settings className="h-4 w-4" />
        </button>
      </div>

      {/* 拼接设置（折叠） */}
      {showSettings && (
        <div className="border-b border-white/10 bg-slate-900/80 px-4 py-3 text-white">
          <div className="grid max-w-3xl gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
                转场效果
              </label>
              <select
                value={transitionType}
                onChange={(e) => setTransitionType(e.target.value as 'none' | 'fade')}
                className="w-full rounded-2xl border border-white/15 bg-slate-800 px-3 py-2 text-sm text-white outline-none transition focus:border-sky-400"
              >
                <option value="fade">淡入淡出（fade）</option>
                <option value="none">无转场</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
                输出分辨率
              </label>
              <select
                value={outputResolution}
                onChange={(e) => setOutputResolution(e.target.value as 'auto' | '720p' | '1080p')}
                className="w-full rounded-2xl border border-white/15 bg-slate-800 px-3 py-2 text-sm text-white outline-none transition focus:border-sky-400"
              >
                <option value="auto">自动（项目内最高）</option>
                <option value="720p">720p</option>
                <option value="1080p">1080p</option>
              </select>
            </div>
            <div className="flex items-end">
              {siteWatermarkEnabled ? (
                <label className="flex cursor-pointer items-center gap-2 text-sm text-white">
                  <input
                    type="checkbox"
                    checked={watermarkEnabled}
                    onChange={(e) => setWatermarkEnabled(e.target.checked)}
                    className="accent-sky-500"
                  />
                  启用站点水印
                </label>
              ) : (
                <span className="text-xs text-white/40">站点水印已由管理员关闭</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 视频区 */}
      <div className="flex flex-1 items-center justify-center overflow-hidden p-4">
        {loading && !composition ? (
          <div className="flex items-center gap-2 text-white/60">
            <Loader2 className="h-5 w-5 animate-spin" /> 加载中
          </div>
        ) : !composition ? (
          <div className="max-w-md text-center text-white/80">
            <Film className="mx-auto mb-3 h-12 w-12 opacity-60" />
            <p className="mb-4">这个项目还没有生成过成片</p>
            <button
              type="button"
              onClick={handleCreateComposition}
              disabled={creating}
              className="mx-auto inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:scale-[1.02] hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />}
              开始合成
            </button>
          </div>
        ) : composition.status === 'pending' || composition.status === 'running' ? (
          <div className="text-center text-white/80">
            <Loader2 className="mx-auto mb-3 h-12 w-12 animate-spin text-sky-200" />
            <p className="text-lg">正在合成成片...</p>
            <p className="mt-1 text-sm text-white/60">合成需要几分钟，本页面会自动刷新</p>
          </div>
        ) : composition.status === 'failed' ? (
          <div className="max-w-md text-center text-white/80">
            <X className="mx-auto mb-3 h-12 w-12 text-rose-400" />
            <p className="mb-2 text-lg">合成失败</p>
            <p className="mb-4 text-sm text-white/60">
              请稍后重试；如多次失败请联系管理员
            </p>
            <button
              type="button"
              onClick={handleCreateComposition}
              disabled={creating}
              className="mx-auto inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:scale-[1.02] hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />}
              重新合成
            </button>
          </div>
        ) : (
          <div className="w-full max-w-4xl space-y-4">
            <video
              ref={videoRef}
              src={videoSrc ? resolveBrowserMediaUrl(videoSrc) : undefined}
              controls
              className="max-h-[60vh] w-full rounded-2xl bg-black shadow-2xl shadow-slate-950/50"
            />

            {/* 底部时间轴 */}
            {timing.length > 0 && (
              <div className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.18em] text-white/50">
                  分镜时间轴
                </div>
                <div className="flex gap-1.5 overflow-x-auto">
                  {timing.map((t) => (
                    <button
                      key={t.shotId}
                      type="button"
                      onClick={() => handleSeekToShot(t.startMs)}
                      className="flex shrink-0 flex-col items-center rounded-xl bg-white/10 px-3 py-2 text-xs text-white/80 transition hover:bg-white/20"
                      title={`跳转到 ${(t.startMs / 1000).toFixed(1)}s`}
                    >
                      <span className="font-semibold">#{t.order + 1}</span>
                      <span className="text-[10px] text-white/50">
                        {(t.startMs / 1000).toFixed(1)}s
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <span className="text-white/60">
                时长 {composition.resultDuration?.toFixed(1) ?? '?'}s · {composition.outputResolution}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleCreateComposition}
                  disabled={creating}
                  className="inline-flex items-center gap-1.5 rounded-2xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />}
                  重新合成
                </button>
                {videoSrc && (
                  <a
                    href={videoSrc ? resolveBrowserMediaUrl(videoSrc, { download: true }) : undefined}
                    download={`director-${projectId}.mp4`}
                    className="inline-flex items-center gap-1.5 rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-slate-950 transition hover:scale-[1.02] hover:bg-sky-50"
                  >
                    <Download className="h-4 w-4" /> 下载 mp4
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
